import Backbone from 'backbone'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import setupDateTimeRangeAutoFix from 'scoreboard/datasets/filters/setup-datetime-range-autofix'
import DatasetsDateFilterPicker from './datasets-date-filter-picker'
import deleteNonApplicableFilterProperties from './delete-non-applicable-filter-properties'
import momentHelper from 'scoreboard/components/moment-helper'
import formHelper from 'scoreboard/components/form-helper'
import valuesOrEquationsTemplate from 'scoreboard/datasets/filters/datasets-datetime-values-or-equations.hbs'
import { renderValueOrEquations } from './value-or-equation'
import DataType from 'scoreboard/datasets/model/data-type'
const { DATE, TIME, DATE_TIME, CALENDAR_PERIOD } = DataType

/**
 * A date/time picker for dataset fields which are of type date/time/date+time.
 */
const DatasetsDateTimeFilterPicker = DatasetsDateFilterPicker.extend({
	className: DatasetsDateFilterPicker.prototype.className + ' datasets-date-time-filter-picker',

	events: _.extend({}, DatasetsDateFilterPicker.prototype.events, {
		'input [name=time1], [name=time2]': 'fixBadTimes',
		'input [name=dateTime1], [name=dateTime2]': 'fixBadDateTimes'
	}),

	render: function () {
		DatasetsDateFilterPicker.prototype.render.apply(this, arguments)
		this.renderDateOrDateTimeFilterControl(TIME)
		this.renderDateOrDateTimeFilterControl(DATE_TIME)
		return this
	},

	/**
	 * @param {'time' | 'date-time'} type
	 */
	renderDateOrDateTimeFilterControl(type) {
		this._insertDateTimeValueOrEquationsMarkup(type)
		const valueOrEquations = renderValueOrEquations({
			dataType: type,
			model: this.model,
			filter: this.options.filter,
			formView: this.formView,
			field: this.field,
			el: this.$el,
			allowCalculated: this.options.allowCalculated,
			selector: `.${type}-value-or-equation`,
			timeDisplayType: _.result(this.field, 'timeDisplayType')
		})

		const controls = [
			valueOrEquations[0].markedField, valueOrEquations[1].markedField
		]
		setupDateTimeRangeAutoFix(controls, dt => this._shouldFix(dt))

		if (type === TIME) {
			this.timeValueOrEquations = valueOrEquations
		} else if (type === DATE_TIME) {
			this.dateTimeValueOrEquations = valueOrEquations
		}
	},

	/**
	 * @param {'time' | 'date-time'} type
	 */
	_insertDateTimeValueOrEquationsMarkup: function(type) {
		const firstInputLabel = this._isBetweenDateTime() ?
			i18n.get('report.customReport.filter.from') :
			i18n.get(`common.${_.camelCase(type)}`)
		this.$('.toggler-rows.filter-type').append(valuesOrEquationsTemplate({
			firstInputLabel,
			type,
			namePrefix: _.camelCase(type),
			classes: `${type}-filter`
		}))
	},

	// If _fixBadDatesIsIrrelevant or if the selected date/time isIncomplete, we
	// shouldn't go about fixing the input. In all other cases, we should.
	_shouldFix(dt) {
		// If this date time event was triggered by the date time control's final
		// trigger before being removed, we should go ahead and fix the times.
		// If _fixBadDatesIsIrrelevant then don't fix the dates.
		if (dt.isBeingRemoved && !this._fixBadDatesIsIrrelevant()) {
			return true
		}

		return false
	},

	_getTimeInput: function(ordinal) {
		return this.timeValueOrEquations[ordinal].markedField
	},

	_getDateTimeInput: function(ordinal) {
		return this.dateTimeValueOrEquations[ordinal].markedField
	},

	val() {
		const fromDom = formHelper.readFromDom(this.el)
		if (this.chooseByVal() === TIME) {
			_.extend(this.options.filter, fromDom, { dataType: TIME })
			deleteNonApplicableFilterProperties(this.options.filter, this.chooseByVal())
			return this.options.filter
		} else if (this.chooseByVal() === 'date-time') {
			// #35379 Interpret the selected value as local, (default), and convert it
			// into field-native zone.
			const read = control => {
				const asMoment = _.result(control, 'getDateTime.moment')
				if (asMoment) {
					return asMoment.tz(this.field.timeZone || momentHelper.defaultTimeZone())
						.format(momentHelper.serverLocalDateTimeFormat)
				}
			}
			deleteNonApplicableFilterProperties(this.options.filter, this.chooseByVal())
			const dateTime1 = read(this._getDateTimeInput(0))
			const dateTime2 = read(this._getDateTimeInput(1))
			const chosenDateTimes =
				_.omitBy({ dateTime1, dateTime2 }, it => !it)
			return _.extend(this.options.filter, fromDom, chosenDateTimes, {
				dataType: DATE_TIME
			})
		} else {
			// Set the dataType according to the toggle value (#54272)
			const dataType = this.toggle.val() === CALENDAR_PERIOD ? CALENDAR_PERIOD : DATE
			const dateFilter =
				DatasetsDateFilterPicker.prototype.val.apply(this, arguments)
			return _.extend(dateFilter, { dataType })
		}
	},

	_getToggleOptions() {
		return [{
			value: CALENDAR_PERIOD,
			label: 'common.calendar.calendarPeriod'
		},{
			value: 'day',
			label: 'common.date'
		},{
			value: TIME,
			label: 'common.time'
		},{
			value: DATE_TIME,
			label: 'datasets.datatype.groups.dateTime'
		}]
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap(this.timeValueOrEquations, 'remove')
		_.invokeMap(this.dateTimeValueOrEquations, 'remove')
	}
})

export default DatasetsDateTimeFilterPicker

DatasetsDateTimeFilterPicker.__filename__ = 'datasets/filters/datasets-date-time-filter-picker.js'
